import { collection, doc, getDocs, limit, onSnapshot, orderBy, query, where, type Unsubscribe } from 'firebase/firestore';
import type { InquiryDoc, InquiryStatus, MessageDoc } from '@blauturquesa/shared';
import type { RoleLite } from '@blauturquesa/shared/roles-lite';
import { db } from '../../firebase/firestore';

export type InquiryRow = InquiryDoc & { id: string };
export type MessageRow = MessageDoc & { id: string; at: number };

const PAGE = 100;

/**
 * Consultes del perfil actiu, amb el `where` que exigeixen les regles (§20.1): el client les seves,
 * l'agent les de les cases on té accés, el superadmin totes. Les obertes s'escolten en temps real
 * (dades vives); les tancades es llegeixen sota demanda.
 */
function inquiriesQuery(uid: string, role: RoleLite | null, status: InquiryStatus) {
  const base = collection(db, 'inquiries');
  if (role === 'client') return query(base, where('clientUid', '==', uid), where('status', '==', status), limit(PAGE));
  if (role === 'superadmin') return query(base, where('status', '==', status), limit(PAGE));
  return query(base, where('accessUids', 'array-contains', uid), where('status', '==', status), limit(PAGE));
}

const rowsOf = (docs: { id: string; data: () => unknown }[]) =>
  docs.map((d) => ({ id: d.id, ...(d.data() as InquiryDoc) }))
    // Ordenat al client: orderBy() exclouria documents sense el camp (§20.1).
    .sort((a, b) => (b.lastMessage?.at ?? '').localeCompare(a.lastMessage?.at ?? ''));

export function watchOpenInquiries(uid: string, role: RoleLite | null, onRows: (rows: InquiryRow[]) => void, onError: () => void): Unsubscribe {
  return onSnapshot(inquiriesQuery(uid, role, 'open'), (snap) => onRows(rowsOf(snap.docs)), onError);
}

export async function getClosedInquiries(uid: string, role: RoleLite | null): Promise<InquiryRow[]> {
  return rowsOf((await getDocs(inquiriesQuery(uid, role, 'closed'))).docs);
}

/** La conversa oberta: el document i els missatges, en temps real (§3.2: només la conversa oberta). */
export function watchInquiry(id: string, onDoc: (q: InquiryRow | null) => void, onError: () => void): Unsubscribe {
  return onSnapshot(doc(db, 'inquiries', id), (s) => onDoc(s.exists() ? { id: s.id, ...(s.data() as InquiryDoc) } : null), onError);
}

export function watchMessages(id: string, onRows: (rows: MessageRow[]) => void, onError: () => void): Unsubscribe {
  return onSnapshot(query(collection(db, 'inquiries', id, 'messages'), orderBy('createdAt', 'asc'), limit(500)), { includeMetadataChanges: false }, (snap) => {
    onRows(snap.docs.map((d) => {
      const data = d.data({ serverTimestamps: 'estimate' }) as MessageDoc & { createdAt?: { toMillis?: () => number } };
      return { id: d.id, ...data, at: data.createdAt?.toMillis?.() ?? Date.now() };
    }));
  }, onError);
}

/** Comptador agregat de no llegits (`users/{uid}.unreadTotal`, §20.1): una sola lectura per canvi. */
export function watchUnreadTotal(uid: string, onTotal: (n: number) => void): Unsubscribe {
  return onSnapshot(doc(db, 'users', uid), (s) => onTotal(Math.max(0, Number(s.get('unreadTotal') ?? 0))), () => onTotal(0));
}
